import { useEffect } from 'react';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Image, Progress, Typography, Upload, UploadFile } from 'antd';
import { RcFile, UploadChangeParam, UploadProps } from 'antd/es/upload';
import { useProcBasicFormContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { Col, Row } from '../layout';

const { Text } = Typography;
const supportedImageTypes = ['image/jpeg', 'image/png'];

export const ImageUpload = () => {
  const {
    setUploadedFileKey,
    setImageLink,
    uploadedImageLink,
    setUploadedImageLink,
    fileList,
    setFileList,
    uploadStatus,
    setUploadStatus,
    uploadProgress,
    setUploadProgress,
    setIsUploading,
  } = useProcBasicFormContext();

  const { showNotification } = useNotificationContext();

  const beforeUpload = (file: RcFile) => {
    const isSupportedType = supportedImageTypes.includes(file.type);
    if (!isSupportedType) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: `You can only upload ${supportedImageTypes
          .map((type) => type.substring(type.indexOf('/') + 1).toUpperCase())
          .join('/')} file!`,
      });
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'Image must be smaller than 10MB!',
      });
    }
    if (isLt10M && isSupportedType) {
      setFileList([file]);
    }
    return false;
  };

  const handleChange: UploadProps['onChange'] = (
    info: UploadChangeParam<UploadFile>,
  ) => {
    setUploadProgress(0);
    setUploadStatus(undefined);
    if (info.file.status === 'uploading') {
      setIsUploading(true);
    }
    if (info.file.status === 'done') {
      const file = info.file.response;
      setUploadedFileKey(file.fileKey);
      setIsUploading(false);
    }
  };

  useEffect(() => {
    // remove image preview after new image added
    if (fileList?.length! > 0) {
      setImageLink('');
      setUploadedImageLink('');
    }
  }, [fileList]);

  return (
    <div style={{ marginBottom: 20 }}>
      <Upload
        name="file"
        fileList={fileList}
        listType="text"
        showUploadList={true}
        onRemove={() => {
          setFileList([]);
          setUploadProgress(0);
        }}
        maxCount={1}
        beforeUpload={beforeUpload}
        onChange={handleChange}
      >
        <Col alignItems="flex-start">
          <Button
            data-testid="file-upload-button"
            icon={<UploadOutlined />}
            style={{ marginTop: 10 }}
          >
            Select File
          </Button>
          <Text
            style={{
              fontSize: 11,
              marginTop: 5,
              marginBottom: 10,
              opacity: 0.7,
            }}
          >
            Supported file types:{' '}
            {supportedImageTypes
              .map((type) => type.substring(type.indexOf('/') + 1))
              .join(', ')}
          </Text>
        </Col>
      </Upload>
      {Boolean(uploadProgress) && (
        <Progress percent={uploadProgress} size="small" status={uploadStatus} />
      )}
      {uploadedImageLink && (
        <Row>
          <Image
            src={uploadedImageLink}
            alt="image"
            style={{ width: 200, marginTop: 20 }}
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => {
              setUploadedImageLink('');
              setUploadedFileKey('');
            }}
          />
        </Row>
      )}
    </div>
  );
};
